import keys from '@debbie/common/dist/cache/keys.js'
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js'
import helpers from '@debbie/common/dist/economy/helpers/index.js'
import { BankAccountType } from '@debbie/common/dist/index.js'
import * as customersAPI from '@debbie/cortex/dist/api/customers.js'
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js'
import { useCase } from '@debbie/cortex/dist/cases/index.js'
import { useCollector } from '@debbie/cortex/dist/collectors/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import {
  customersEntity,
  ensureCustomerLoaded,
  useCustomer,
  usePendingCustomerOverpayments,
} from '@debbie/cortex/dist/customers/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import BankAccount from '@debbie/iris/dist/components/economy/BankAccount.js'
import { Container } from '@debbie/iris/dist/components/Grid/index.js'
import {
  Button,
  Content,
  Money,
  PageBase,
  Panel,
  PlaceholderLoader,
} from '@debbie/iris/dist/components/index.js'
import { updateTheme } from '@debbie/iris/dist/theme.js'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { APIBASE } from '../../config.js'
import { AccountInput } from './CaseStyles.js'

const Overpayment = () => {
  const { id } = useParams<{ id: string }>()

  const caseIdLoader = useCase(id)
  const caseId = h.hasData(caseIdLoader) ? caseIdLoader.data.caseId : null
  const singleCaseLoader = useCase(caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null

  const lang = useSelector((s: RootState) => s.lang)
  const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId)
  const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null
  const customerLoader = useCustomer(singleCase?.customerId)
  const customer = h.hasData(customerLoader) ? customerLoader.data : null

  const pendingOverpayments = usePendingCustomerOverpayments(
    customer?.customerId,
  )
  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null

  const collectorLoadable = useCollector(
    (singleCase &&
      creditor &&
      getCollectorOnCase(
        singleCase.type,
        singleCase.collectorId,
        creditor.identityCollectorId,
        creditor.defaultCollectorId,
      )) ||
      undefined,
  )
  const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null

  const [editingBankAccount, setEditingBankAccount] = useState<boolean>(false)
  const [accountNumber, setAccountNumber] = useState<[string, string]>(['', ''])

  const [themeUpdated, setThemeUpdated] = useState<boolean>(false)
  useEffect(() => {
    if (collector) {
      updateTheme(collector.theme)
      setThemeUpdated(true)
    }
  }, [collector])

  const onSubmit = (e: React.FormEvent, verify: boolean = false) => {
    e.stopPropagation()
    e.preventDefault()

    if (!customer || !singleCase) {
      return
    }

    const changed =
      customer.bankAccount?.accountNumber !== accountNumber[1] ||
      customer.bankAccount?.registrationNumber !== accountNumber[0]

    customersAPI
      .updateBankAccount(
        singleCase.customerId,
        verify
          ? undefined
          : changed
            ? {
                type: BankAccountType.Danish,
                registrationNumber: accountNumber[0],
                accountNumber: accountNumber[1],
              }
            : undefined,
      )
      .then(async () => {
        setEditingBankAccount(false)

        q.invalidate(keys.write.modify(customersEntity, singleCase!.customerId))
        await ensureCustomerLoaded(singleCase!.customerId)
        setAccountNumber(['', ''])
      })
  }

  const overpayment = caseVouchers && helpers.overview.balance(caseVouchers) > 0

  const bankAccountExists = customer?.bankAccount != null

  const bankAccountNeedsVerification =
    h.hasData(pendingOverpayments) && pendingOverpayments.data.length > 0

  return (
    <div>
      <Content fullHeight>
        <PageBase>
          <Container style={{ maxWidth: '500px', textAlign: 'center' }}>
            <Panel>
              {!(
                collector &&
                singleCase &&
                creditor &&
                customer &&
                caseVouchers
              ) ? (
                <>
                  <PlaceholderLoader width="70%" />
                  <PlaceholderLoader width="50%" />
                  <PlaceholderLoader width="55%" />
                </>
              ) : (
                <>
                  <div style={{ margin: '2em 0em 1em 0em' }}>
                    {collector &&
                      (collector.logo ? (
                        <img
                          style={{ maxHeight: '70px' }}
                          alt={collector.logo.fileName}
                          src={`${APIBASE}/files/${collector.logo.fileId}/${collector.logo.fileName}`}
                        />
                      ) : (
                        <h3>{collector.name}</h3>
                      ))}
                  </div>

                  {overpayment && (
                    <>
                      {bankAccountExists ? (
                        <>
                          <h1>{lang.debtorWeb.overpaymentSuccessHeader}</h1>
                          <p>{lang.debtorWeb.overpaymentSuccessTextHeader}</p>
                        </>
                      ) : (
                        <>
                          <h1>{lang.debtorWeb.overpaymentHeader}</h1>
                          <p>
                            {lang.debtorWeb.overpaymentPreAmount}{' '}
                            {singleCase.currency}{' '}
                            <Money
                              amount={helpers.overview.balance(caseVouchers)}
                            />{' '}
                            {lang.debtorWeb.overpaymentPostAmount}
                          </p>
                        </>
                      )}
                      {bankAccountExists && !editingBankAccount ? (
                        <>
                          <BankAccount
                            editable={true}
                            onEdit={() => setEditingBankAccount(true)}
                            accountDetails={customer!.bankAccount!}
                          />
                          {themeUpdated && bankAccountNeedsVerification && (
                            <>
                              <br />
                              <Button
                                onClick={(e) => {
                                  onSubmit(e, true)
                                }}
                              >
                                {lang.misc.submit}
                              </Button>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <form
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onSubmit={onSubmit}
                          >
                            <AccountInput
                              onChange={(accountNumber) =>
                                setAccountNumber(accountNumber)
                              }
                              value={accountNumber}
                            />
                            <Button
                              style={{
                                marginBottom: '20px',
                                padding: '13px',
                              }}
                            >
                              {lang.misc.submit}
                            </Button>
                          </form>
                          {editingBankAccount && (
                            <Button
                              theme="secondary"
                              size="small"
                              onClick={() => setEditingBankAccount(false)}
                            >
                              {lang.misc.cancel}
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Panel>
          </Container>
        </PageBase>
      </Content>
    </div>
  )
}

export default Overpayment
